/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * State holds calendar KEYS (YYYY-MM-DD). Dates handed to date-fns or the Calendar are
 * browser-local tokens from keyToLocalDate; instants are made only through
 * zoneMidnight/wallClockToInstant and read only through calendarDateKey/gymWallClock.
 */
import { format } from "date-fns";
import {
  addDaysToKey,
  calendarDateKey,
  clocksDiffer,
  isCalendarDateKey,
  wallClockParts,
  wallClockToInstant,
  weekStartKey,
  zoneMidnight,
} from "@lemonadetraining/contracts";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const WEEK_DAYS = 7;

const pad = (n: number) => String(n).padStart(2, "0");

export const hhmm = (hour: number, minute: number): string => `${pad(hour)}:${pad(minute)}`;

// Noon, so a zone that skips local midnight still lands on the right calendar day.
export const keyToLocalDate = (key: string): Date => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d, 12);
};

export const localDateToKey = (date: Date): string => format(date, "yyyy-MM-dd");

export const addGymDays = (key: string, days: number): string => {
  const next = addDaysToKey(key, days);
  if (next === null) throw new Error(`addGymDays: cannot add ${days} days to ${key}`);
  return next;
};

/** Monday of the key's week. */
export const gymWeekStart = (key: string): string => {
  const start = weekStartKey(key, 1);
  if (start === null) throw new Error(`gymWeekStart: no week for ${key}`);
  return start;
};

/** The instant a stored gym-day date should hold. Throws rather than guess a day. */
export const gymKeyMidnight = (key: string, zone: string): Date => {
  const ms = zoneMidnight(key, zone);
  if (ms === null) throw new Error(`gymKeyMidnight: no midnight for ${key} in ${zone}`);
  return new Date(ms);
};

export const gymDayKey = (
  instant: Date | number | null | undefined,
  zone: string | null
): string | null =>
  instant === null || instant === undefined || !zone ? null : calendarDateKey(instant, zone);

/** Whole days on `zone`'s calendar; `toZone` is the calendar of the programme it lands in. */
export const shiftGymDays = (
  instant: Date | number,
  days: number,
  zone: string,
  toZone: string = zone
): Date => {
  const key = addDaysToKey(calendarDateKey(instant, zone), days);
  if (key === null) throw new Error(`shiftGymDays: unreadable instant in ${zone}`);
  return gymKeyMidnight(key, toZone);
};

/** [startMs, endMs) from the first day's gym midnight to the midnight after the last day. */
export const gymKeySpan = (fromKey: string, toKeyInclusive: string, zone: string): InstantRange => {
  const startMs = zoneMidnight(fromKey, zone);
  const endMs = zoneMidnight(addDaysToKey(toKeyInclusive, 1), zone);
  if (startMs === null || endMs === null || endMs <= startMs) {
    throw new Error(`gymKeySpan: no span from ${fromKey} to ${toKeyInclusive} in ${zone}`);
  }
  return { startMs, endMs };
};

/** [startMs, endMs) of one gym day. */
export const gymKeyWindow = (key: string, zone: string): InstantRange => gymKeySpan(key, key, zone);

export interface GymWallClock {
  key: string;
  hour: number;
  minute: number;
  hhmm: string;
}

export const gymWallClock = (instantMs: number, zone: string): GymWallClock | null => {
  const parts = wallClockParts(instantMs, zone);
  if (!parts) return null;
  const { year, month, day, hour, minute } = parts;
  return { key: `${year}-${pad(month)}-${pad(day)}`, hour, minute, hhmm: hhmm(hour, minute) };
};

/** `message` is the whole error: the caller names what could not be read. */
export const gymWallClockOrThrow = (
  instantMs: number,
  zone: string,
  message: string
): GymWallClock => {
  const wall = gymWallClock(instantMs, zone);
  if (!wall) throw new Error(message);
  return wall;
};

export type ResolvedWallClock =
  | { status: "empty" }
  | { status: "invalid" }
  | { status: "gap" }
  | { status: "ok"; instantMs: number };

export const resolveWallClock = (
  dateKey: string | null,
  time: string,
  zone: string | null
): ResolvedWallClock => {
  if (!dateKey || !time) return { status: "empty" };
  const result = wallClockToInstant(dateKey, time, zone);
  if ("instant" in result) return { status: "ok", instantMs: result.instant };
  return { status: result.reason };
};

export interface GymWeekDay {
  key: string;
  date: Date;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
}

export interface GymWeek {
  startKey: string;
  zone: string;
  startMs: number;
  endMs: number;
  days: GymWeekDay[];
  /** Day column 0..6 of an instant, clamped to the week. */
  columnOf: (instantMs: number) => number;
  /** Minutes since gym midnight on the gym's wall clock; NaN for an unreadable instant. */
  minutesOf: (instantMs: number) => number;
}

export const gymWeek = (startKey: string, zone: string, nowMs: number): GymWeek => {
  const startMs = isCalendarDateKey(startKey) ? zoneMidnight(startKey, zone) : null;
  const endMs = startMs === null ? null : zoneMidnight(addDaysToKey(startKey, WEEK_DAYS), zone);
  if (startMs === null || endMs === null) {
    throw new Error(`gymWeek: cannot build a week from ${startKey} in ${zone}`);
  }
  const todayKey = calendarDateKey(nowMs, zone);
  const days = Array.from({ length: WEEK_DAYS }, (_, i) => {
    const key = addDaysToKey(startKey, i) as string;
    const date = keyToLocalDate(key);
    return {
      key,
      date,
      dayName: format(date, "EEE"),
      dayNumber: date.getDate(),
      isToday: key === todayKey,
    };
  });
  const keys = days.map((d) => d.key);
  return {
    startKey,
    zone,
    startMs,
    endMs,
    days,
    columnOf: (instantMs) => {
      if (instantMs < startMs) return 0;
      if (instantMs >= endMs) return WEEK_DAYS - 1;
      return Math.max(0, keys.indexOf(calendarDateKey(instantMs, zone) as string));
    },
    minutesOf: (instantMs) => {
      const wall = wallClockParts(instantMs, zone);
      return wall ? wall.hour * 60 + wall.minute : NaN;
    },
  };
};

export type GymDayPattern = "EEE" | "EEEE" | "EEE d MMM" | "EEEE d MMM";

/** The gym-calendar day an instant falls on, as a date-fns day label. */
export const formatGymDay = (instantMs: number, zone: string, pattern: GymDayPattern): string => {
  const key = calendarDateKey(instantMs, zone);
  return key ? format(keyToLocalDate(key), pattern) : "";
};

export interface InstantRange {
  startMs: number;
  /** Exclusive. */
  endMs: number;
}

const differAt = (zoneA: string, zoneB: string, ms: number) => clocksDiffer(zoneA, zoneB, [ms]);

/** First minute in (lo, hi] whose answer is no longer `before`. */
const firstChange = (zoneA: string, zoneB: string, lo: number, hi: number, before: boolean) => {
  let a = lo;
  let b = hi;
  while (b - a > MINUTE_MS) {
    const mid = a + Math.floor((b - a) / 2 / MINUTE_MS) * MINUTE_MS;
    if (differAt(zoneA, zoneB, mid) === before) a = mid;
    else b = mid;
  }
  return b;
};

/** Stretches of [fromMs, toMs) where the two zones' wall clocks read differently. */
export const differingSegments = (
  zoneA: string,
  zoneB: string,
  fromMs: number,
  toMs: number
): InstantRange[] => {
  const segments: InstantRange[] = [];
  let inside = differAt(zoneA, zoneB, fromMs);
  let start = fromMs;
  for (let prev = fromMs; prev < toMs - 1;) {
    const at = Math.min(prev + HOUR_MS, toMs - 1);
    if (differAt(zoneA, zoneB, at) !== inside) {
      const edge = firstChange(zoneA, zoneB, prev, at, inside);
      if (inside) segments.push({ startMs: start, endMs: edge });
      else start = edge;
      inside = !inside;
    }
    prev = at;
  }
  if (inside) segments.push({ startMs: start, endMs: toMs });
  return segments;
};
